import React, { useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import {
  ARTIST_BIO_LONG_MAX,
  SPOTIFY_BIO_RIGHTS_TEXT,
  type ArtistProfileResponse,
  type SpotifyBioUploadResponse,
} from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { bioCounter } from '../bio/bioRules';
import { profileBioMessages as b } from '../bio/profileBioMessages';
import { SpotifyBioStateLine } from './SpotifyBioStateLine';
import { spotifyBioMessages as s } from './spotifyBioMessages';
import { spotifyUploadMessages as u } from './spotifyUploadMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifyBioSendFormProps {
  profile: ArtistProfileResponse;
  artist: string;
  isSending: boolean;
  error: string | null;
  done: SpotifyBioUploadResponse | null;
  onSend: (acceptRights: boolean) => void;
  onCancel: () => void;
}

/**
 * The bio sheet with a bio to send: the saved long bio as Spotify gets it, its upload state, the
 * rights line of the Spotify bio editor with its tick before the first bio upload of the artist,
 * Send. After Send: the result, and Close.
 */
const SpotifyBioSendFormComponent: React.FC<SpotifyBioSendFormProps> = (props) => {
  const { profile, artist, isSending, error, done, onSend, onCancel } = props;
  const intl = useIntl();
  const [rights, setRights] = useState(false);
  const { bioLong } = profile.bio;
  const counter = bioCounter(bioLong, ARTIST_BIO_LONG_MAX);
  const rightsText = profile.spotify.bio.rightsText === '' ? SPOTIFY_BIO_RIGHTS_TEXT : profile.spotify.bio.rightsText;
  const needsRights = !profile.spotify.bio.rightsAccepted;
  const canSend = (!needsRights || rights) && !isSending && counter.over === 0;

  return (
    <div className="d-flex flex-column gap-3" data-testid="spotify-bio-send">
      <span className={styles.meta}>{intl.formatMessage(s.what, { artist })}</span>
      <div className={styles.bioField}>
        <div className={styles.bioHead}>
          <b>{intl.formatMessage(b.longTitle)}</b>
          <span className={clsx(styles.count, counter.over > 0 && styles.countOver)}>
            {intl.formatMessage(b.counter, { count: counter.count, max: ARTIST_BIO_LONG_MAX })}
          </span>
        </div>
        <div className={clsx(styles.text, styles.textTall)} data-testid="spotify-bio-text">
          {bioLong}
        </div>
      </div>
      <SpotifyBioStateLine profile={profile} />
      {needsRights && done === null && (
        <div className={styles.rights} data-testid="spotify-bio-rights">
          <div className="form-check m-0">
            <input
              id="spotify-bio-rights-tick"
              type="checkbox"
              className="form-check-input"
              checked={rights}
              data-testid="spotify-bio-rights-tick"
              onChange={(event) => setRights(event.currentTarget.checked)}
            />
            <label className="form-check-label" htmlFor="spotify-bio-rights-tick">
              <b>{intl.formatMessage(s.rightsTick)}</b>
            </label>
          </div>
          <span>
            {rightsText} {intl.formatMessage(s.rightsOnce)}
          </span>
        </div>
      )}
      {error !== null && (
        <div className={styles.error} role="alert" data-testid="spotify-bio-error">
          {error}
        </div>
      )}
      {done !== null && (
        <div className={styles.meta} role="status" data-testid="spotify-bio-done">
          {intl.formatMessage(done.confirmed ? s.doneConfirmed : s.doneUnconfirmed)}
        </div>
      )}
      <div className={styles.row}>
        {done === null ? (
          <>
            <SMLButton variant="primary" disabled={!canSend} onClick={() => onSend(needsRights && rights)}>
              {intl.formatMessage(isSending ? s.sendBusy : s.send)}
            </SMLButton>
            <SMLButton onClick={onCancel}>{intl.formatMessage(u.cancel)}</SMLButton>
            <span className={styles.grow} />
            <span className={styles.meta}>{intl.formatMessage(s.replaces)}</span>
          </>
        ) : (
          <SMLButton variant="primary" onClick={onCancel}>
            {intl.formatMessage(u.close)}
          </SMLButton>
        )}
      </div>
    </div>
  );
};

export const SpotifyBioSendForm = TypedMemo(SpotifyBioSendFormComponent);
